import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Chip,
  Skeleton,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import SearchIcon from '@mui/icons-material/Search';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { inventoryAPI } from '../services/api';
import { useNotify } from '../context/NotifyContext';
import errorMessage from '../utils/errorMessage';
import { useSkeletonAnimation } from '../components/LoadingSkeletons';

// ── Single item row ───────────────────────────────────────────────────────────

function ItemRow({ item, pendingQty, onChange }) {
  const displayQty = pendingQty ?? item.quantity;
  const hasChange = pendingQty !== undefined;

  const adjust = (delta) => {
    const next = Math.max(0, parseFloat(displayQty || 0) + delta);
    onChange(item.id, next);
  };

  const handleInput = (e) => {
    const raw = e.target.value;
    if (raw === '' || raw === '-') {
      onChange(item.id, raw);
      return;
    }
    const num = parseFloat(raw);
    if (!isNaN(num) && num >= 0) onChange(item.id, num);
  };

  return (
    <Paper
      elevation={hasChange ? 3 : 1}
      sx={{
        p: 2,
        mb: 1.5,
        borderRadius: 2,
        border: hasChange ? '2px solid' : '2px solid transparent',
        borderColor: hasChange ? 'primary.main' : 'transparent',
        transition: 'border-color 0.15s',
      }}
    >
      {/* Header row */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <Box sx={{ flex: 1, mr: 1 }}>
          <Typography variant="subtitle1" fontWeight={600} lineHeight={1.2}>
            {item.item_name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {[item.category_name, item.storage_location].filter(Boolean).join(' · ')}
          </Typography>
        </Box>
        {item.low_stock && (
          <Chip
            icon={<WarningAmberIcon />}
            label="Low"
            color="warning"
            size="small"
            sx={{ fontWeight: 600 }}
          />
        )}
      </Box>

      {/* Stepper row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconButton
          onClick={() => adjust(-1)}
          size="large"
          sx={{ bgcolor: 'action.hover', borderRadius: 2, width: 48, height: 48 }}
        >
          <RemoveIcon />
        </IconButton>

        <TextField
          value={displayQty}
          onChange={handleInput}
          inputProps={{
            inputMode: 'decimal',
            style: { textAlign: 'center', fontSize: '1.25rem', fontWeight: 600, padding: '8px 0' },
          }}
          sx={{ width: 90 }}
          size="small"
        />

        <IconButton
          onClick={() => adjust(1)}
          size="large"
          sx={{ bgcolor: 'action.hover', borderRadius: 2, width: 48, height: 48 }}
        >
          <AddIcon />
        </IconButton>

        <Typography variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
          {item.unit || ''}
        </Typography>

        {hasChange && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
            was {item.quantity}
          </Typography>
        )}
      </Box>
    </Paper>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

// Same card size as ItemRow (name, caption, then the +/- quantity controls).
function ItemListSkeleton() {
  const animation = useSkeletonAnimation();
  return [0, 1, 2, 3, 4].map((i) => (
    <Paper key={i} elevation={1} sx={{ p: 2, mb: 1.5, borderRadius: 2, border: '2px solid transparent' }} aria-hidden="true">
      <Skeleton animation={animation} width="55%" height={24} />
      <Skeleton animation={animation} width="35%" sx={{ mb: 1 }} />
      <Skeleton animation={animation} variant="rounded" height={40} />
    </Paper>
  ));
}

export default function InventoryCountPage() {
  const notify = useNotify();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [changes, setChanges] = useState({});
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await inventoryAPI.getAll({ active: true });
      const data = Array.isArray(res.data) ? res.data : (res.data.items || []);
      setItems(data.sort((a, b) => {
        const typeCompare = (a.item_type || '').localeCompare(b.item_type || '');
        return typeCompare !== 0 ? typeCompare : a.item_name.localeCompare(b.item_name);
      }));
    } catch (error) {
      notify.error(errorMessage(error, 'Failed to load inventory'));
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => { load(); }, [load]);

  const categories = ['All', ...Array.from(new Set(
    items.map(i => i.category_name).filter(Boolean)
  )).sort()];

  const filtered = items.filter(item => {
    const matchesSearch = !search ||
      item.item_name.toLowerCase().includes(search.toLowerCase()) ||
      (item.category_name || '').toLowerCase().includes(search.toLowerCase()) ||
      (item.storage_location || '').toLowerCase().includes(search.toLowerCase());
    const matchesCategory = activeCategory === 'All' || item.category_name === activeCategory;
    return matchesSearch && matchesCategory;
  });

  // Group by type; within each group, low-stock items float to the top
  const grouped = filtered.reduce((acc, item) => {
    const key = item.item_type || 'Uncategorized';
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});
  const groupedSorted = Object.entries(grouped)
    .sort(([a], [b]) => {
      if (a === 'Other') return 1;
      if (b === 'Other') return -1;
      return a.localeCompare(b);
    })
    .map(([type, groupItems]) => ({
      type,
      items: [
        ...groupItems.filter(i => i.low_stock),
        ...groupItems.filter(i => !i.low_stock),
      ],
    }));

  const handleChange = (id, value) => {
    setChanges(prev => {
      const originalItem = items.find(i => i.id === id);
      const num = parseFloat(value);
      if (!isNaN(num) && num === originalItem?.quantity) {
        const { [id]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [id]: value };
    });
  };

  const pendingCount = Object.keys(changes).length;

  const handleSave = async () => {
    setSaving(true);
    const entries = Object.entries(changes);
    const validEntries = entries.filter(([, v]) => !isNaN(parseFloat(v)) && parseFloat(v) >= 0);

    if (validEntries.length < entries.length) {
      notify.warning('Some values are invalid and were skipped');
    }

    try {
      await Promise.all(
        validEntries.map(([id, qty]) => inventoryAPI.update(id, { quantity: parseFloat(qty) }))
      );
      setItems(prev => prev.map(item => {
        const updated = validEntries.find(([id]) => parseInt(id) === item.id);
        return updated ? { ...item, quantity: parseFloat(updated[1]) } : item;
      }));
      const savedCount = validEntries.length;
      setChanges({});
      notify.success(`${savedCount} item${savedCount !== 1 ? 's' : ''} updated`);
    } catch (error) {
      notify.error(errorMessage(error, 'Save failed — please try again'));
    }
    setSaving(false);
  };

  return (
    <Box sx={{ pb: pendingCount > 0 ? 10 : 2 }}>
      <Typography variant="h6" fontWeight={700} gutterBottom>
        Stock Count
      </Typography>

      {/* Search */}
      <TextField
        size="small"
        fullWidth
        placeholder="Search items, categories, locations…"
        value={search}
        onChange={e => setSearch(e.target.value)}
        sx={{ mb: 1.5 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      {/* Category chips */}
      <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 1, mb: 1.5, flexWrap: 'nowrap' }}>
        {categories.map(cat => (
          <Chip
            key={cat}
            label={cat}
            onClick={() => setActiveCategory(cat)}
            color={activeCategory === cat ? 'primary' : 'default'}
            variant={activeCategory === cat ? 'filled' : 'outlined'}
            sx={{ flexShrink: 0 }}
          />
        ))}
      </Box>

      {/* Pending changes summary */}
      {pendingCount > 0 && (
        <Typography variant="caption" color="primary" sx={{ display: 'block', mb: 1 }}>
          {pendingCount} unsaved change{pendingCount !== 1 ? 's' : ''}
        </Typography>
      )}

      {/* Item list */}
      {loading ? (
        <ItemListSkeleton />
      ) : groupedSorted.length === 0 ? (
        <Typography color="text.secondary" sx={{ mt: 4, textAlign: 'center' }}>
          No items match your search.
        </Typography>
      ) : (
        groupedSorted.map(({ type, items: groupItems }) => (
          <Box key={type}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, mb: 1 }}>
              <Typography variant="overline" fontWeight={700} color="text.secondary" lineHeight={1}>
                {type}
              </Typography>
              <Divider sx={{ flex: 1 }} />
            </Box>
            {groupItems.map(item => (
              <ItemRow
                key={item.id}
                item={item}
                pendingQty={changes[item.id] !== undefined ? changes[item.id] : undefined}
                onChange={handleChange}
              />
            ))}
          </Box>
        ))
      )}

      {/* Sticky save button */}
      {pendingCount > 0 && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            p: 2,
            bgcolor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'divider',
            zIndex: 1200,
          }}
        >
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving…' : `Save ${pendingCount} change${pendingCount !== 1 ? 's' : ''}`}
          </Button>
        </Box>
      )}
    </Box>
  );
}
